// ============================================================================
// LIVE SCREEN MODAL — admin watches a user's screen live (WebRTC viewer)
// ============================================================================
// Opens from the Work Activity → user detail page. Flow:
//   1. ask LMT backend for a short "live token"  (GET /lmt/timeLogger/live-token)
//   2. connect to the Time-Logger signaling server (socket.io) with that token
//   3. 'live:request' {targetUserId}  → the user's desktop makes us an OFFER
//   4. we ANSWER, trade ICE candidates, then the screen video arrives on
//      pc.ontrack and plays in <video>. Nothing is stored; closing ends it.
// The video is peer-to-peer and encrypted; the signaling server only brokers it.
// ============================================================================

import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { io } from "socket.io-client";
import { getLiveStreamToken } from "../../services/timeTracker.service";

// Where the Time-Logger signaling server lives. Set REACT_APP_TIMELOGGER_URL in
// the LMT frontend .env (e.g. https://timelogger.lamynaals.com). Defaults to the
// local dev server.
const TIMELOGGER_URL = process.env.REACT_APP_TIMELOGGER_URL || "http://localhost:4000";

// STUN finds public IPs; TURN relays the video when the admin and the user are on
// different networks and can't connect directly. TURN is added only if the env
// vars are set (so a LAN keeps using direct P2P, no relay cost).
//   REACT_APP_TURN_URL   e.g. turn:103.174.103.168:3478
//   REACT_APP_TURN_USER / REACT_APP_TURN_PASS
const ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];
if (process.env.REACT_APP_TURN_URL) {
  ICE_SERVERS.push({
    urls: process.env.REACT_APP_TURN_URL,
    username: process.env.REACT_APP_TURN_USER,
    credential: process.env.REACT_APP_TURN_PASS,
  });
}

const LiveScreenModal = ({ open, onClose, userId, userName }) => {
  const videoRef = useRef(null);
  const socketRef = useRef(null);
  const pcRef = useRef(null);
  const streamerIdRef = useRef(null);
  // 'connecting' | 'waiting' | 'live' | 'offline' | 'on-break' | 'clocked-out' | 'forbidden' | 'error'
  const [status, setStatus] = useState("connecting");

  // "offline" must not clobber a more specific end-reason (on-break / clocked-out):
  // the desktop tears the peer down right after sending the reason, and that
  // connection-state change would otherwise overwrite the message.
  const softOffline = () =>
    setStatus((s) => (s === "on-break" || s === "clocked-out" ? s : "offline"));

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setStatus("connecting");

    (async () => {
      let token;
      try {
        const res = await getLiveStreamToken(userId);
        token = res?.token || res?.data?.token;
      } catch (e) {
        if (!cancelled) setStatus(e?.response?.status === 403 ? "forbidden" : "error");
        return;
      }
      if (cancelled || !token) return;

      const socket = io(TIMELOGGER_URL, {
        auth: { token, client: "web" },
        // Try polling first, then upgrade to websocket. If the reverse proxy in
        // front of the TL-V2 server isn't configured for WS upgrade, the socket
        // still connects over polling (signaling = tiny text messages, so polling
        // is perfectly fine). websocket-only would hard-fail in that case.
        transports: ["polling", "websocket"],
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        setStatus("waiting");
        socket.emit("live:request", { targetUserId: userId }, (resp) => {
          if (!resp?.ok) {
            const known = ["forbidden", "on-break", "clocked-out"];
            setStatus(known.includes(resp?.reason) ? resp.reason : "offline");
          }
        });
      });

      // Desktop made us an offer → answer it and start receiving the screen.
      socket.on("live:offer", async ({ from, sdp }) => {
        streamerIdRef.current = from;
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        pc.onicecandidate = (ev) => {
          if (ev.candidate) socket.emit("live:ice", { to: from, candidate: ev.candidate });
        };
        pc.ontrack = (ev) => {
          if (videoRef.current) videoRef.current.srcObject = ev.streams[0];
          setStatus("live");
        };
        pc.onconnectionstatechange = () => {
          if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
            softOffline();
          }
        };
        await pc.setRemoteDescription(sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("live:answer", { to: from, sdp: pc.localDescription });
      });

      socket.on("live:ice", async ({ candidate }) => {
        if (pcRef.current) { try { await pcRef.current.addIceCandidate(candidate); } catch {} }
      });
      socket.on("live:stop", () => softOffline());
      socket.on("live:peer-gone", () => softOffline());
      // User went on break / clocked out (at request time, or mid-stream — the
      // desktop cuts the video and tells us why).
      socket.on("live:unavailable", ({ reason }) => {
        try { pcRef.current?.close(); } catch {}
        pcRef.current = null;
        setStatus(reason === "on-break" || reason === "clocked-out" ? reason : "offline");
      });
      socket.on("connect_error", () => setStatus("error"));
    })();

    // Cleanup when the modal closes / unmounts → end the session cleanly.
    return () => {
      cancelled = true;
      try {
        if (streamerIdRef.current && socketRef.current) {
          socketRef.current.emit("live:stop", { to: streamerIdRef.current });
        }
      } catch {}
      try { pcRef.current?.close(); } catch {}
      pcRef.current = null;
      streamerIdRef.current = null;
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    };
  }, [open, userId]);

  const message = {
    connecting: "Connecting…",
    waiting: "Waiting for the user's screen…",
    live: "Live",
    offline: "User is offline or stopped sharing.",
    "on-break": "User is on break — live viewing is paused until they resume work.",
    "clocked-out": "User has clocked out for the day — live viewing is not available.",
    forbidden: "You don't have permission to watch this user.",
    error: "Connection error.",
  }[status];

  // Re-ask for the stream on the SAME socket — used after "on break" / "clocked
  // out" / "offline" so the admin can check again without closing the modal.
  const retry = () => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    setStatus("waiting");
    socket.emit("live:request", { targetUserId: userId }, (resp) => {
      if (!resp?.ok) {
        const known = ["forbidden", "on-break", "clocked-out"];
        setStatus(known.includes(resp?.reason) ? resp.reason : "offline");
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          component="span"
          sx={{
            width: 10, height: 10, borderRadius: "50%",
            bgcolor: status === "live" ? "#e64a4a" : "#9ca3af",
            boxShadow: status === "live" ? "0 0 0 4px rgba(230,74,74,.2)" : "none",
          }}
        />
        Live Screen — {userName || "User"}
        <IconButton onClick={onClose} sx={{ ml: "auto" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: "relative", background: "#000", borderRadius: 2, overflow: "hidden",
            aspectRatio: "16 / 9", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%", height: "100%", objectFit: "contain",
              display: status === "live" ? "block" : "none",
            }}
          />
          {status !== "live" && (
            <Box sx={{ color: "#cbd5e1", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              {(status === "connecting" || status === "waiting") && <CircularProgress size={26} sx={{ color: "#cbd5e1" }} />}
              <Typography variant="body2">{message}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {["on-break", "clocked-out", "offline"].includes(status) && (
          <Button onClick={retry} variant="outlined">
            Check Again
          </Button>
        )}
        {/* Closing the dialog already ends the session (cleanup runs on unmount);
            this is just an explicit, obvious way to stop watching. */}
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          startIcon={<StopCircleIcon />}
        >
          Stop Watching
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiveScreenModal;
